import { SegmentedControl } from '@ai-tutor/ui';
import type { SegmentedOption } from '@ai-tutor/ui';

import type { ProgressPeriod } from '@/lib/api-client';

interface PeriodToggleProps {
  period: ProgressPeriod;
  onChange: (period: ProgressPeriod) => void;
  /** When set, the toggle is collapsed because the user is viewing a past year. */
  viewingPastYear?: number;
  onClearPastYear?: () => void;
}

// Sentence case, like every other label in the product.
export const PERIOD_OPTIONS: readonly SegmentedOption<ProgressPeriod>[] = [
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'this_year', label: 'This year' },
];

export function PeriodToggle({
  period,
  onChange,
  viewingPastYear,
  onClearPastYear,
}: PeriodToggleProps) {
  if (viewingPastYear !== undefined) {
    return (
      <div className='inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5'>
        <span className='text-xs font-medium text-muted-foreground'>
          Viewing {viewingPastYear}
        </span>
        {onClearPastYear && (
          <button
            onClick={onClearPastYear}
            // brand-700, not brand-500. This button sits on the page
            // GROUND (the header strip, not a card), where brand-500
            // measures 2.77:1 and fails AA outright — the ramp's own note in
            // globals.css puts 400-500 at icons and 600-700 at text, and this
            // is text. brand-700 is 5.76:1 there.
            className='text-xs font-medium text-brand-700 hover:text-brand-800 dark:text-primary dark:hover:text-primary'
          >
            ← Current
          </button>
        )}
      </div>
    );
  }

  // A real radiogroup, not a row of bare buttons - see `SegmentedControl`.
  // This used to hand-roll the pills with no `role`, no `aria-checked` and no
  // keyboard navigation at all.
  return (
    <SegmentedControl
      label='Time period'
      options={PERIOD_OPTIONS}
      value={period}
      onChange={onChange}
      size='md'
    />
  );
}
